import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FixedSizeList as List } from 'react-window';
import { useClickLock, generateRequestId } from '../hooks';
import { trackMessageSent, trackVoiceNoteRecorded, trackMediaSent, trackCallInitiated, trackFirstConversation, trackFeatureUsed } from '@/utils/analytics';

// Chat components (extracted for maintainability)
import {
  MessageStatus,
  VoiceNotePlayer,
  MediaMessage,
  SCROLL_BOTTOM_THRESHOLD,
  SCROLL_TOP_THRESHOLD,
  SCROLL_DEBOUNCE_MS,
  MEDIA_PLACEHOLDER_HEIGHT,
  MEDIA_PLACEHOLDER_WIDTH,
  EMOJI_CATEGORIES,
  REACTION_EMOJIS,
  getTimerDisplay,
  formatLastSeen,
  formatDateSeparator,
} from '@/components/chat';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  ArrowLeft, Send, Lock, Phone, Video, MoreVertical, User, 
  Clock, Trash2, Timer, CheckCheck, LogOut, Settings, Shield,
  Image as ImageIcon, Paperclip, Mic, Camera, Smile, X, Play, Pause, Eye,
  Flame, Heart, ThumbsUp, MessageCircle, Reply, Volume2, Check,
  AlertCircle, Loader2, ChevronDown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import CallInterface from '@/components/calls/CallInterface';
import { 
  initChatCache, 
  addMessageToCache,
  getPendingMessages,
  addPendingMessage,
  removePendingMessage,
  cacheMessages,
  getCachedMessages
} from '@/utils/chatCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Chat() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get preloaded conversation from navigation state for instant display
  const preloadedConv = location.state?.preloadedConversation;
  
  const [conversation, setConversation] = useState(preloadedConv || null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [user, setUser] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);  // For immediate isMine check
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);  // True until first messages load
  const [showCallInterface, setShowCallInterface] = useState(false);
  const [callType, setCallType] = useState('audio');
  const [incomingCall, setIncomingCall] = useState(null);
  const [pendingWebrtcOffer, setPendingWebrtcOffer] = useState(null);  // Store offer until CallInterface mounts
  const socketRef = useRef(null);
  const [showDisappearingSettings, setShowDisappearingSettings] = useState(false);
  const [disappearingTimer, setDisappearingTimer] = useState('7d');
  
  // Pagination & virtualization
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const oldestMessageIdRef = useRef(null);
  
  // Media states
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);  // Track upload in progress
  const [viewOnce, setViewOnce] = useState(false);
  const [viewOnceDuration, setViewOnceDuration] = useState(0);
  const [showViewOnceViewer, setShowViewOnceViewer] = useState(false);
  const [viewOnceMedia, setViewOnceMedia] = useState(null);
  const [viewOnceCountdown, setViewOnceCountdown] = useState(0);
  const [failedMediaMessage, setFailedMediaMessage] = useState(null);  // Store failed media for retry
  
  // Emoji picker
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiCategory, setEmojiCategory] = useState('recent');
  
  // Typing indicator
  const [typingUsers, setTypingUsers] = useState([]);
  const typingTimeoutRef = useRef(null);
  
  // Voice recording - WhatsApp style
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [listenOnce, setListenOnce] = useState(false); // NEW: Listen-once mode for voice notes
  const mediaRecorderRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const audioChunksRef = useRef([]);
  const isCancelledRef = useRef(false);
  const recordingSessionIdRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const recordingStartTimeRef = useRef(null);  // Track actual start time
  
  // Reply
  const [replyingTo, setReplyingTo] = useState(null);
  
  // Swipe-to-reply gesture tracking
  const [swipingMessageId, setSwipingMessageId] = useState(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const swipeStartRef = useRef({ x: 0, y: 0 });
  const isSwipingRef = useRef(false);
  const SWIPE_THRESHOLD = 60; // Minimum swipe distance to trigger reply
  
  // Reactions
  const [showReactions, setShowReactions] = useState(null);
  
  // Message deletion
  const [showDeleteMenu, setShowDeleteMenu] = useState(null);  // Message ID for delete menu
  const [deletingMessage, setDeletingMessage] = useState(false);
  
  // Full-screen media viewer
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [mediaViewerContent, setMediaViewerContent] = useState(null); // { type: 'image'|'video'|'audio', url: string }
  
  // Upload cancellation
  const uploadAbortControllerRef = useRef(null);
  const [canCancelUpload, setCanCancelUpload] = useState(false);
  
  // Online status of other user
  const [otherUserOnline, setOtherUserOnline] = useState(false);
  const [otherUserLastSeen, setOtherUserLastSeen] = useState(null);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const isSubmittingRef = useRef(false);  // Prevent duplicate submissions
  
  // ============================================
  // SCROLL STATE v2 - Simplified architecture
  // ============================================
  const shouldAutoScrollRef = useRef(true);  // TRUE = user at bottom, scroll on new message
  const [showScrollButton, setShowScrollButton] = useState(false);  // Down arrow button
  const [unreadCount, setUnreadCount] = useState(0);  // Count of new messages while scrolled up
  const scrollLockRef = useRef(false);  // Debounce lock
  const isLoadingOlderRef = useRef(false);  // Prevent multiple load triggers
  const initialScrollDoneRef = useRef(false);  // Track initial scroll
  const lastMessageCountRef = useRef(0);  // Track message count for change detection

  // ============================================
  // PREVENT BODY SCROLL - Only chat container scrolls
  // ============================================
  useEffect(() => {
    // Lock body scroll for full-screen chat experience
    const originalOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    return () => {
      // Restore scroll on unmount
      document.body.style.overflow = originalOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, []);

  // ============================================
  // iOS KEYBOARD HANDLING - Fix viewport issues
  // ============================================
  // ===== SCROLL UTILITIES (Clean implementation) =====
  const isAtBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  }, []);

  const scrollToBottom = useCallback((force = false) => {
    const el = messagesContainerRef.current;
    if (!el) return;
    
    if (force || isAtBottom()) {
      el.scrollTop = el.scrollHeight;
    }
  }, [isAtBottom]);

  // ===== CHAT PAGE SCROLL LOCK (Scoped to this page only) =====
  useEffect(() => {
    // Lock body scroll for chat page (prevents iOS keyboard issues)
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    return () => {
      // Unlock when leaving chat page
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
    };
  }, []);

  // ===== iOS CHROME SPECIFIC FIX =====
  // Chrome on iOS has different viewport behavior than Safari
  useEffect(() => {
    const isIOSChrome = /CriOS/.test(navigator.userAgent);
    if (!isIOSChrome) return;

    // Set custom height property for iOS Chrome
    const setAppHeight = () => {
      document.documentElement.style.setProperty(
        '--app-height',
        `${window.innerHeight}px`
      );
    };

    setAppHeight();
    window.addEventListener('resize', setAppHeight);

    // Lock body position during keyboard interaction
    const handleFocus = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.top = '0';
      }
    };

    const handleBlur = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        // Reset scroll position
        window.scrollTo(0, 0);
      }
    };

    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);

    return () => {
      window.removeEventListener('resize', setAppHeight);
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
      document.documentElement.style.removeProperty('--app-height');
    };
  }, []);

  // iOS Safari Keyboard stabilizer - simple viewport resize handler
  useEffect(() => {
    const handleViewportResize = () => {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    };

    window.visualViewport?.addEventListener('resize', handleViewportResize);
    
    return () => {
      window.visualViewport?.removeEventListener('resize', handleViewportResize);
    };
  }, [scrollToBottom]);

  // Get user ID from token immediately (for isMine check before API loads)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Decode JWT to get user ID instantly
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.sub) {
          setCurrentUserId(payload.sub);
          console.log('[Chat] Got user ID from token:', payload.sub);
        }
      } catch (e) {
        console.error('[Chat] Failed to decode token:', e);
      }
    }
  }, []);

  // Socket.IO initialization for real-time features
  useEffect(() => {
    if (!user || !conversationId) return;
    
    // Create socket with auth containing user_id (per your spec STEP 1)
    if (!socketRef.current) {
      const { io } = require('socket.io-client');
      socketRef.current = io(process.env.REACT_APP_BACKEND_URL, {
        path: '/api/socket.io',
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        auth: {
          user_id: String(user.id)  // STEP 1: Pass user_id in auth
        }
      });
      console.log('[Chat] Socket created with auth user_id:', user.id);
    }
    
    const socket = socketRef.current;
    
    // Remove existing listeners first to prevent duplicates
    socket.off('connect');
    socket.off('registered');
    socket.off('incoming_call');
    socket.off('webrtc_offer');
    socket.off('new_message');
    socket.off('message_sent');
    socket.off('message_delivered');
    socket.off('message_read');
    socket.off('messages_read');
    socket.off('user_typing');
    socket.off('user_stop_typing');
    socket.off('user_status_change');
    
    // STEP 1: Connection handler
    socket.on('connect', () => {
      console.log('[Chat] Socket connected:', socket.id);
      // Register user to join their own room (STEP 2)
      socket.emit('register_user', { user_id: String(user.id) });
      socket.emit('join', { conversation_id: conversationId });
    });
    
    // Confirmation that user joined their room
    socket.on('registered', (data) => {
      console.log('[Chat] User registered in room:', data);
    });
    
    // If already connected, register immediately
    if (socket.connected) {
      console.log('[Chat] Socket already connected:', socket.id);
      socket.emit('register_user', { user_id: String(user.id) });
      socket.emit('join', { conversation_id: conversationId });
    }
      
    // Handle incoming calls (DO NOT CHANGE - per user request)
    socket.on('incoming_call', (data) => {
      console.log('Incoming call:', data);
      if (showCallInterface) {
        console.log('Call interface already showing, ignoring duplicate incoming_call');
        return;
      }
      setIncomingCall(data);
      setCallType(data.call_type);
      setShowCallInterface(true);
      toast.info(`Incoming ${data.call_type} call from ${data.caller_name}`);
    });
    
    // Capture WebRTC offer (DO NOT CHANGE - per user request)
    socket.on('webrtc_offer', (data) => {
      console.log('[Chat] Captured webrtc_offer - storing for CallInterface');
      setPendingWebrtcOffer(data);
    });
    
    // STEP 5: Receive new message - THIS IS THE KEY HANDLER
    socket.on('new_message', (message) => {
      console.log('[Chat] *** NEW MESSAGE RECEIVED ***', message);
      
      // Add message to UI IMMEDIATELY if it's for current conversation
      if (message.conversation_id === conversationId) {
        // Check if at bottom BEFORE adding message
        const wasAtBottom = isAtBottom();
        
        setMessages(prev => {
          // Skip if already exists (by id or temp_id)
          if (prev.some(m => m.id === message.id || (m.temp_id && m.temp_id === message.temp_id))) {
            console.log('[Chat] Message already exists, skipping');
            return prev;
          }
          // Skip own messages (already shown via optimistic UI)
          if (message.from_user_id === user?.id) {
            console.log('[Chat] Own message, skipping (shown via optimistic UI)');
            return prev;
          }
          console.log('[Chat] Adding new message to UI');
          return [...prev, message];
        });
        
        // Only scroll to bottom if user was already at bottom
        if (wasAtBottom) {
          requestAnimationFrame(() => {
            scrollToBottom();
          });
        }
      }
      
      // If we are the receiver, emit delivered confirmation
      if (message.to_user_id === user?.id && message.id) {
        console.log('[Chat] Emitting message_delivered for:', message.id);
        socket.emit('message_delivered', { 
          message_id: message.id,
          sender_id: message.from_user_id 
        });
        
        // Also mark as read since chat is open
        socket.emit('mark_read', {
          message_ids: [message.id],
          sender_id: message.from_user_id,
          reader_id: user.id
        });
      }
    });
    
    // STEP 6: Sender gets confirmation message was sent
    socket.on('message_sent', (data) => {
      console.log('[Chat] message_sent confirmation:', data);
      setMessages(prev => prev.map(msg => {
        if (msg.temp_id === data.temp_id) {
          return { ...data.message, temp_id: data.temp_id, status: 'sent' };
        }
        return msg;
      }));
      removePendingMessage(data.temp_id);
    });
    
    // Handle message delivered - double gray tick (single message)
    socket.on('message_delivered', (data) => {
      console.log('[Chat] message_delivered:', data.message_id);
      setMessages(prev => prev.map(msg => 
        msg.id === data.message_id ? { ...msg, status: 'delivered' } : msg
      ));
    });
    
    // Handle BATCH messages delivered (when receiver comes online)
    socket.on('messages_delivered', (data) => {
      console.log('[Chat] messages_delivered (batch):', data.message_ids?.length);
      const messageIds = data.message_ids || [];
      setMessages(prev => prev.map(msg => 
        messageIds.includes(msg.id) ? { ...msg, status: 'delivered' } : msg
      ));
    });
    
    // Handle message read - double BLUE tick (single message)
    socket.on('message_read', (data) => {
      console.log('[Chat] message_read:', data);
      const messageIds = data.message_ids || [data.message_id];
      setMessages(prev => prev.map(msg => 
        messageIds.includes(msg.id) ? { ...msg, status: 'read' } : msg
      ));
    });
    
    // Handle BATCH messages seen (when receiver opens chat)
    socket.on('messages_seen', (data) => {
      console.log('[Chat] messages_seen (batch):', data.message_ids?.length);
      const messageIds = data.message_ids || [];
      setMessages(prev => prev.map(msg => 
        messageIds.includes(msg.id) ? { ...msg, status: 'read' } : msg
      ));
    });
    
    // Backwards compatibility
    socket.on('messages_read', (data) => {
      console.log('[Chat] messages_read:', data.message_ids);
      setMessages(prev => prev.map(msg => 
        data.message_ids?.includes(msg.id) ? { ...msg, status: 'read' } : msg
      ));
    });
    
    // Handle typing indicators
    socket.on('user_typing', (data) => {
      setTypingUsers(prev => {
        if (!prev.includes(data.username)) {
          return [...prev, data.username];
        }
        return prev;
      });
    });
    
    socket.on('user_stop_typing', (data) => {
      setTypingUsers(prev => prev.filter(u => u !== data.username));
    });
    
    // Handle user online status changes
    socket.on('user_status_change', (data) => {
      const otherUserId = conversation?.user1_id === user?.id 
        ? conversation?.user2_id 
        : conversation?.user1_id;
      
      if (data.user_id === otherUserId) {
        setOtherUserOnline(data.is_online);
        if (data.last_seen) {
          setOtherUserLastSeen(data.last_seen);
        }
      }
    });
    
    // Handle view-once message opened by recipient - mark as opened on sender's chat
    socket.on('view_once_opened', (data) => {
      console.log('[Chat] View-once message opened:', data);
      if (data.conversation_id === conversationId) {
        // Mark the message as opened (NOT remove it)
        setMessages(prev => prev.map(m => 
          m.id === data.message_id 
            ? { ...m, view_once_opened: true, view_once_opened_at: data.opened_at }
            : m
        ));
        toast.info('View-once media was viewed');
      }
    });
    
    // Handle message deleted by other user
    socket.on('message_deleted', (data) => {
      console.log('[Chat] Message deleted:', data);
      if (data.conversation_id === conversationId) {
        if (data.delete_type === 'for_everyone') {
          // Mark message as deleted for everyone
          setMessages(prev => prev.map(m => 
            m.id === data.message_id 
              ? { ...m, deleted_for_everyone: true, deleted_for_everyone_at: new Date().toISOString() }
              : m
          ));
        }
      }
    });
    
    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave', { conversation_id: conversationId });
        socketRef.current.off('connect');
        socketRef.current.off('registered');
        socketRef.current.off('incoming_call');
        socketRef.current.off('webrtc_offer');
        socketRef.current.off('new_message');
        socketRef.current.off('message_sent');
        socketRef.current.off('message_delivered');
        socketRef.current.off('messages_delivered');
        socketRef.current.off('message_read');
        socketRef.current.off('messages_read');
        socketRef.current.off('messages_seen');
        socketRef.current.off('user_typing');
        socketRef.current.off('user_stop_typing');
        socketRef.current.off('user_status_change');
        socketRef.current.off('view_once_opened');
        socketRef.current.off('message_deleted');
      }
    };
  }, [user, conversationId]);
  
  // Mark all messages as SEEN when user opens this chat
  // This uses the new mark_chat_seen socket event
  const [hasMarkedChatSeen, setHasMarkedChatSeen] = useState(false);
  
  useEffect(() => {
    // Reset hasMarkedChatSeen when conversation changes
    setHasMarkedChatSeen(false);
  }, [conversationId]);
  
  // Reset scroll state when conversation changes
  useEffect(() => {
    shouldAutoScrollRef.current = true;
    setShowScrollButton(false);
    setUnreadCount(0);
    initialScrollDoneRef.current = false;
    lastMessageCountRef.current = 0;
    isLoadingOlderRef.current = false;
  }, [conversationId]);
  
  useEffect(() => {
    const socket = socketRef.current;
    if (hasMarkedChatSeen || !socket || !user?.id || !conversationId) {
      return;
    }
    
    const markChatAsSeen = () => {
      console.log('[Chat] Emitting mark_chat_seen for conversation:', conversationId);
      socket.emit('mark_chat_seen', {
        conversation_id: conversationId,
        reader_id: user.id
      });
      setHasMarkedChatSeen(true);
    };
    
    // Check if socket is connected
    if (socket.connected) {
      // Small delay to ensure socket is fully registered
      setTimeout(markChatAsSeen, 500);
    } else {
      // Wait for connect event
      const onConnect = () => {
        setTimeout(markChatAsSeen, 500);
      };
      socket.once('connect', onConnect);
      return () => socket.off('connect', onConnect);
    }
  }, [conversationId, user?.id, hasMarkedChatSeen, socketRef.current?.connected]);

  // Cleanup socket on page unload (browser close/refresh)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (socketRef.current) {
        socketRef.current.emit('logout');
        socketRef.current.disconnect();
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Handle page visibility change (phone locked/unlocked)
  // Refresh messages when user returns to the app
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Chat] Page became visible - refreshing messages');
        
        // Reconnect socket if disconnected
        if (socketRef.current && !socketRef.current.connected) {
          console.log('[Chat] Socket disconnected - reconnecting');
          socketRef.current.connect();
        }
        
        // Fetch fresh messages (silent refresh)
        fetchConversation(true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [conversationId]);

  // Handle incoming call from navigation state (when user clicks "Answer" from Dashboard)
  useEffect(() => {
    if (location.state?.incomingCall && conversation) {
      // Only show call interface after conversation is loaded
      console.log('[Chat] Incoming call from navigation state:', location.state.incomingCall);
      setIncomingCall(location.state.incomingCall);
      setCallType(location.state.incomingCall.call_type);
      setShowCallInterface(true);
      // Clear the state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, conversation]);  // Added conversation dependency

  useEffect(() => {
    // Run essential API calls in parallel for faster initial load
    const loadChat = async () => {
      // fetchUser only needed if we don't have user data
      const userPromise = !user ? fetchUser() : Promise.resolve();
      const convPromise = fetchConversation();
      
      // Run in parallel
      await Promise.all([userPromise, convPromise]);
      
      // Non-critical: fetch settings after main content loads
      fetchSettings();
    };
    
    loadChat();
    
    // Check permissions in background (non-blocking)
    checkMediaPermissions();
    
    // Only refresh typing status periodically
    const typingInterval = setInterval(() => {
      fetchTypingStatus();
    }, 3000);
    
    // Cleanup function - called when user leaves the conversation
    return () => {
      clearInterval(typingInterval);
      // Note: Don't call leaveConversation here - it causes issues on hot reloads
      // The beforeunload handler takes care of actual page leaves
    };
  }, [conversationId]);
  
  // Separate effect for expiry check that depends on disappearing settings
  useEffect(() => {
    if (!conversation?.disappearing_enabled) return;
    
    // Check for expired messages when disappearing is enabled
    const expiryCheckInterval = setInterval(() => {
      setMessages(prev => {
        const now = new Date();
        const activeMessages = prev.filter(msg => {
          if (!msg.expires_at) return true;
          return new Date(msg.expires_at) > now;
        });
        if (activeMessages.length !== prev.length) {
          console.log('[Chat] Removed', prev.length - activeMessages.length, 'expired messages');
          return activeMessages;
        }
        return prev;
      });
    }, 5000);
    
    return () => clearInterval(expiryCheckInterval);
  }, [conversation?.disappearing_enabled]);

  // Call this when user leaves the conversation to expire view-once messages
  const leaveConversation = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token && conversationId) {
        await axios.post(`${API}/messages/conversation/${conversationId}/leave`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('[Chat] Left conversation, view-once messages expired');
      }
    } catch (error) {
      console.log('[Chat] Error leaving conversation:', error);
    }
  };

  // Handle back button click - navigate INSTANTLY, cleanup in background
  const handleBack = useCallback(() => {
    // Navigate immediately - don't wait for API
    navigate('/dashboard');
    
    // Cleanup in background (non-blocking)
    leaveConversation().catch(err => {
      console.log('[Chat] Background cleanup error (ignored):', err);
    });
  }, [navigate, conversationId]);

  // Handle page unload (closing tab, refreshing, etc.)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable delivery when page is unloading
      const token = localStorage.getItem('token');
      if (token && conversationId) {
        navigator.sendBeacon(
          `${API}/messages/conversation/${conversationId}/leave`,
          new Blob([JSON.stringify({})], { type: 'application/json' })
        );
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [conversationId]);

  // Check and request media permissions early
  const checkMediaPermissions = async () => {
    try {
      // Check if permissions API is available
      if (navigator.permissions) {
        const cameraStatus = await navigator.permissions.query({ name: 'camera' });
        const micStatus = await navigator.permissions.query({ name: 'microphone' });
        
        console.log('[Chat] Camera permission:', cameraStatus.state);
        console.log('[Chat] Microphone permission:', micStatus.state);
        
        // If permissions are not granted, show a helpful toast
        if (cameraStatus.state === 'prompt' || micStatus.state === 'prompt') {
          // Don't auto-prompt, but let user know they'll need permissions for calls
          console.log('[Chat] Media permissions not yet granted');
        }
      }
    } catch (e) {
      // Some browsers don't support querying these permissions
      console.log('[Chat] Permission query not supported:', e);
    }
  };

  // Pre-request media permissions when user first opens chat
  const requestMediaPermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      // Immediately stop the stream - we just wanted permission
      stream.getTracks().forEach(track => track.stop());
      toast.success('Camera and microphone access granted!');
      return true;
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        toast.error('Please allow camera/microphone access for calls');
      }
      return false;
    }
  };

  // ============================================
  // SCROLL SYSTEM v2 - Core Functions
  // ============================================
  
  // Check if user is at or near the bottom
  const isUserAtBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const { scrollTop, scrollHeight, clientHeight } = container;
    return scrollHeight - scrollTop - clientHeight <= SCROLL_BOTTOM_THRESHOLD;
  }, []);

  // Instant scroll to bottom (no animation) - for initial load
  const scrollToBottomInstant = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
      shouldAutoScrollRef.current = true;
      setShowScrollButton(false);
      setUnreadCount(0);
    });
  }, []);
  
  // Smooth scroll to bottom - for button click
  const scrollToBottomSmooth = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth'
    });
    shouldAutoScrollRef.current = true;
    setShowScrollButton(false);
    setUnreadCount(0);
  }, []);

  // ============================================
  // SCROLL EVENT HANDLER - With debouncing
  // ============================================
  const handleScroll = useCallback((e) => {
    // Debounce to prevent multiple triggers
    if (scrollLockRef.current) return;
    scrollLockRef.current = true;
    
    setTimeout(() => {
      scrollLockRef.current = false;
    }, SCROLL_DEBOUNCE_MS);
    
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Check if at bottom
    const atBottom = scrollHeight - scrollTop - clientHeight <= SCROLL_BOTTOM_THRESHOLD;
    shouldAutoScrollRef.current = atBottom;
    
    // Update scroll button visibility (only if changed)
    setShowScrollButton(prev => prev !== !atBottom ? !atBottom : prev);
    
    // Clear unread count when at bottom
    if (atBottom) {
      setUnreadCount(0);
    }
    
    // Load older messages when near top (with lock to prevent multiple triggers)
    if (scrollTop < SCROLL_TOP_THRESHOLD && hasMoreMessages && !isLoadingOlderRef.current && !loadingMore) {
      loadMoreMessages();
    }
  }, [hasMoreMessages, loadingMore]);

  // ============================================
  // INITIAL SCROLL - Only once when messages first load
  // ============================================
  useEffect(() => {
    if (!initialScrollDoneRef.current && messages.length > 0 && !initialLoading) {
      initialScrollDoneRef.current = true;
      // Use double RAF to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToBottomInstant();
        });
      });
    }
  }, [messages.length, initialLoading, scrollToBottomInstant]);

  // ============================================
  // NEW MESSAGE HANDLING - Only scroll if user was at bottom
  // ============================================
  useEffect(() => {
    const currentCount = messages.length;
    const previousCount = lastMessageCountRef.current;
    const isNewMessage = currentCount > previousCount && previousCount > 0;
    
    lastMessageCountRef.current = currentCount;
    
    if (isNewMessage && initialScrollDoneRef.current) {
      const addedCount = currentCount - previousCount;
      
      if (shouldAutoScrollRef.current) {
        // User was at bottom - auto scroll to new message
        requestAnimationFrame(() => {
          scrollToBottomInstant();
        });
      } else {
        // User scrolled up - show unread badge, DON'T scroll
        setUnreadCount(prev => prev + addedCount);
      }
    }
  }, [messages.length, scrollToBottomInstant]);

  // Check if user is near the bottom of chat (legacy support)
  const checkIsAtBottom = useCallback((container) => {
    if (!container) return true;
    const { scrollTop, scrollHeight, clientHeight } = container;
    return scrollHeight - scrollTop - clientHeight < SCROLL_BOTTOM_THRESHOLD;
  }, []);

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      // Only logout on actual auth errors (401 Unauthorized)
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        console.error('Failed to fetch user:', error);
        toast.error('Failed to load profile. Please refresh.');
      }
    }
  };

  // Mark unread messages as read
  const markMessagesAsRead = (msgs) => {
    const socket = socketRef.current;
    if (!socket || !socket.connected || !user?.id) {
      console.log('[Chat] Socket not ready for markMessagesAsRead');
      return;
    }
    
    // Find messages that need to be marked as read:
    // 1. Messages TO us (by to_user_id) that are not read
    // 2. OR messages FROM others (not from us) that are not read (for older msgs without to_user_id)
    const unreadMessageIds = msgs
      .filter(m => {
        const isNotRead = m.status !== 'read';
        const isToUs = m.to_user_id === user.id;
        const isFromOther = m.from_user_id !== user.id;
        // Message is for us if: explicitly to_user_id matches OR it's from someone else
        return isNotRead && (isToUs || (!m.to_user_id && isFromOther));
      })
      .map(m => m.id)
      .filter(Boolean);
    
    if (unreadMessageIds.length > 0) {
      console.log('[Chat] Marking', unreadMessageIds.length, 'messages as read');
      
      // Get sender ID from the first unread message (to notify them)
      const firstUnread = msgs.find(m => unreadMessageIds.includes(m.id));
      const senderId = firstUnread?.from_user_id;
      
      socket.emit('mark_read', {
        conversation_id: conversationId,
        message_ids: unreadMessageIds,
        reader_id: user.id,
        sender_id: senderId  // Also pass sender_id for notification
      });
      
      // Update local state immediately
      setMessages(prev => prev.map(msg => {
        if (unreadMessageIds.includes(msg.id)) {
          return { ...msg, status: 'read' };
        }
        return msg;
      }));
    }
  };
  
  // Mark messages as delivered when chat opens (for messages received while offline)
  const markMessagesAsDelivered = (msgs) => {
    const socket = socketRef.current;
    if (!socket || !socket.connected || !user?.id) {
      console.log('[Chat] Socket not ready for markMessagesAsDelivered');
      return;
    }
    
    // Find messages TO us that are still 'sent' (not yet delivered)
    const sentMessageIds = msgs
      .filter(m => {
        const isSent = m.status === 'sent';
        const isToUs = m.to_user_id === user.id;
        const isFromOther = m.from_user_id !== user.id;
        return isSent && (isToUs || (!m.to_user_id && isFromOther));
      });
    
    // Emit delivered for each message (to update sender's tick)
    sentMessageIds.forEach(msg => {
      if (msg.id && msg.from_user_id) {
        console.log('[Chat] Emitting message_delivered for:', msg.id);
        socket.emit('message_delivered', {
          message_id: msg.id,
          sender_id: msg.from_user_id
        });
      }
    });
  };

  const fetchConversation = async (skipCache = false) => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch fresh conversation data from server with more messages initially
      const response = await axios.get(`${API}/messages/conversation/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 25 }
      });
      
      const conv = response.data.conversation;
      const freshMessages = response.data.messages || [];
      
      // Set conversation for header display
      setConversation(conv);
      
      // Update messages - preserve local state (like view_once_opened from socket events)
      setMessages(prevMessages => {
        // If same count and same last message ID, skip update to prevent flicker
        if (prevMessages.length === freshMessages.length && 
            prevMessages.length > 0 && 
            freshMessages.length > 0 &&
            prevMessages[prevMessages.length - 1]?.id === freshMessages[freshMessages.length - 1]?.id) {
          return prevMessages;
        }
        
        // Merge fresh messages with local state to preserve view_once_opened status
        // that may have been set via socket before the DB was updated
        const prevMessageMap = new Map(prevMessages.map(m => [m.id, m]));
        return freshMessages.map(freshMsg => {
          const prevMsg = prevMessageMap.get(freshMsg.id);
          // If we had view_once_opened locally but server doesn't have it yet, preserve it
          if (prevMsg?.view_once_opened && !freshMsg.view_once_opened) {
            return { ...freshMsg, view_once_opened: true, view_once_opened_at: prevMsg.view_once_opened_at };
          }
          return freshMsg;
        });
      });
      
      setHasMoreMessages(freshMessages.length >= 25);
      if (freshMessages.length > 0) {
        oldestMessageIdRef.current = freshMessages[0]?.id;
      }
      
      // Update online status
      setOtherUserOnline(conv.other_user_online || false);
      setOtherUserLastSeen(conv.other_user_last_seen);
      
      // Cache messages in background (only if disappearing is disabled)
      if (!conv.disappearing_enabled) {
        cacheMessages(conversationId, freshMessages).catch(() => {});
      }
      
      // Mark initial loading complete
      setInitialLoading(false);
      
    } catch (error) {
      console.error('[Chat] Failed to load conversation:', error);
      toast.error('Failed to load conversation');
      setInitialLoading(false);
      navigate('/dashboard');
    }
  };
  
  // Load older messages when scrolling up (infinite scroll)
  const loadMoreMessages = async () => {
    // Prevent multiple simultaneous loads
    if (loadingMore || !hasMoreMessages || isLoadingOlderRef.current) return;
    
    // Get oldest message ID from current messages
    const currentOldestId = messages.length > 0 ? messages[0].id : oldestMessageIdRef.current;
    
    if (!currentOldestId) {
      console.log('[Chat] No oldest message ID, cannot load more');
      return;
    }
    
    // Lock to prevent multiple triggers
    isLoadingOlderRef.current = true;
    
    // SCROLL ANCHORING: Save scroll position BEFORE loading
    const container = messagesContainerRef.current;
    const scrollHeightBefore = container?.scrollHeight || 0;
    const scrollTopBefore = container?.scrollTop || 0;
    
    console.log('[Chat] Loading more messages before:', currentOldestId);
    setLoadingMore(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/messages/conversation/${conversationId}/messages?limit=20&before=${currentOldestId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const olderMessages = response.data.messages || [];
      console.log(`[Chat] Loaded ${olderMessages.length} older messages, has_more: ${response.data.has_more}`);
      
      if (olderMessages.length > 0) {
        // Prepend older messages to the beginning
        setMessages(prev => [...olderMessages, ...prev]);
        // Update the oldest reference for next load
        oldestMessageIdRef.current = olderMessages[0]?.id;
        
        // SCROLL ANCHORING: Restore scroll position AFTER DOM update
        // Use double RAF to ensure DOM is fully updated
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (container) {
              const scrollHeightAfter = container.scrollHeight;
              const heightDiff = scrollHeightAfter - scrollHeightBefore;
              // Maintain visual position by adding the height difference
              container.scrollTop = scrollTopBefore + heightDiff;
            }
          });
        });
      }
      
      setHasMoreMessages(response.data.has_more);
    } catch (error) {
      console.error('[Chat] Failed to load more messages:', error);
      toast.error('Failed to load older messages');
    } finally {
      setLoadingMore(false);
      // Unlock after a delay to prevent rapid re-triggers
      setTimeout(() => {
        isLoadingOlderRef.current = false;
      }, 500);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/conversation/${conversationId}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDisappearingTimer(response.data.disappearing_timer || '7d');
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };

  const fetchTypingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/media/typing/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTypingUsers(response.data.typing_users || []);
    } catch (error) {
      // Silent fail for typing status
    }
  };

  const sendTypingStatus = async (isTyping) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('conversation_id', conversationId);
      formData.append('is_typing', isTyping);
      await axios.post(`${API}/media/typing`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Silent fail
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    sendTypingStatus(true);
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingStatus(false);
    }, 3000);
  };

  const updateDisappearingSettings = async (timer) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/messages/conversation/${conversationId}/disappearing?timer=${timer}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDisappearingTimer(timer);
      setShowDisappearingSettings(false);
      toast.success(`Disappearing messages ${timer === 'off' ? 'turned off' : `set to ${getTimerDisplay(timer)}`}`);
      fetchConversation();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  // Mark audio/video message as played (triggers disappearing for view-once)
  const markMediaPlayed = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/messages/${messageId}/mark-played`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[Chat] Marked media message as played:', messageId);
    } catch (error) {
      console.error('[Chat] Failed to mark media as played:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !selectedMedia) return;
    
    // Prevent double submissions
    if (isSubmittingRef.current) {
      console.log('[Chat] Submission already in progress, ignoring');
      return;
    }

    const messageText = newMessage.trim();
    const mediaFile = selectedMedia;
    const mediaPreviewData = mediaPreview;
    const replyTo = replyingTo;
    
    // Clear input immediately for instant feedback
    setNewMessage('');
    setReplyingTo(null);
    sendTypingStatus(false);
    
    // Generate temp ID for optimistic message
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    if (mediaFile) {
      // Media message - show upload progress
      isSubmittingRef.current = true;
      setLoading(true);
      setIsUploading(true);  // Track upload in progress
      setCanCancelUpload(true);
      
      // Create abort controller for cancellation
      const abortController = new AbortController();
      uploadAbortControllerRef.current = abortController;
      
      // Store media info for potential retry
      const mediaInfo = {
        file: mediaFile,
        preview: mediaPreviewData,
        viewOnce: viewOnce,
        viewOnceDuration: viewOnceDuration,
        caption: messageText,
        replyTo: replyTo
      };
      
      // Add optimistic media message
      const optimisticMediaMsg = {
        id: tempId,
        temp_id: tempId,
        from_user_id: user.id,
        message: messageText || '',
        message_type: mediaPreviewData?.type || 'image',
        media_urls: [mediaPreviewData?.url],
        created_at: new Date().toISOString(),
        status: 'sending',
        view_once: viewOnce,
        reply_to: replyTo ? { id: replyTo.id, message: replyTo.message } : null
      };
      
      setMessages(prev => [...prev, optimisticMediaMsg]);
      
      // Scroll to bottom immediately after sending (force scroll)
      requestAnimationFrame(() => {
        scrollToBottom(true);
      });
      
      try {
        const token = localStorage.getItem('token');
        console.log('[Chat] Uploading media:', mediaFile.name, mediaFile.size);
        
        const mediaFormData = new FormData();
        mediaFormData.append('file', mediaFile);
        mediaFormData.append('conversation_id', conversationId);
        mediaFormData.append('view_once', viewOnce);
        mediaFormData.append('view_once_duration', viewOnceDuration);
        
        const uploadResponse = await axios.post(`${API}/media/upload`, mediaFormData, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 180000,
          signal: abortController.signal,  // Add abort signal
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });
        
        const mediaData = uploadResponse.data.media;
        
        // Send media message
        const msgFormData = new FormData();
        msgFormData.append('media_url', mediaData.url);
        if (mediaData.thumbnail_url) msgFormData.append('thumbnail_url', mediaData.thumbnail_url);
        msgFormData.append('message_type', mediaData.media_type);
        msgFormData.append('caption', messageText);
        msgFormData.append('view_once', viewOnce);
        msgFormData.append('view_once_duration', viewOnceDuration);
        if (mediaData.dimensions) msgFormData.append('dimensions', JSON.stringify(mediaData.dimensions));
        if (replyTo) msgFormData.append('reply_to_id', replyTo.id);
        
        const msgResponse = await axios.post(`${API}/messages/conversation/${conversationId}/media`, msgFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Update optimistic message with real data
        setMessages(prev => prev.map(m => 
          m.temp_id === tempId 
            ? { ...msgResponse.data.message, status: 'sent' }
            : m
        ));
        
        // Cache the real message
        addMessageToCache(conversationId, msgResponse.data.message);
        
        // SUCCESS: Clear media preview and view-once settings
        setSelectedMedia(null);
        setMediaPreview(null);
        setViewOnce(false);
        setViewOnceDuration(0);
        setUploadProgress(0);
        setFailedMediaMessage(null);  // Clear any stored failed media
        
      } catch (uploadError) {
        console.error('[Chat] Upload failed:', uploadError);
        
        // Check if upload was cancelled
        if (uploadError.name === 'CanceledError' || uploadError.code === 'ERR_CANCELED') {
          // Remove the optimistic message on cancel
          setMessages(prev => prev.filter(m => m.temp_id !== tempId));
          // Don't show error toast for intentional cancel
          return;
        }
        
        // Mark message as failed
        setMessages(prev => prev.map(m => 
          m.temp_id === tempId ? { ...m, status: 'failed', _mediaInfo: mediaInfo } : m
        ));
        
        // Store failed media for retry
        setFailedMediaMessage({ tempId, ...mediaInfo });
        
        let errorMessage = 'Failed to upload media. Tap retry to try again.';
        if (uploadError.code === 'ECONNABORTED') {
          errorMessage = 'Upload timed out. Tap retry to try again.';
        } else if (uploadError.response?.status === 413) {
          errorMessage = 'File too large.';
        } else if (uploadError.response?.data?.detail) {
          errorMessage = uploadError.response.data.detail;
        }
        toast.error(errorMessage);
      } finally {
        setLoading(false);
        setUploadProgress(0);
        setIsUploading(false);  // Upload finished (success or fail)
        setCanCancelUpload(false);
        uploadAbortControllerRef.current = null;
        isSubmittingRef.current = false;
      }
      
    } else {
      // TEXT MESSAGE - OPTIMISTIC UI (instant display)
      
      // Create optimistic message
      const optimisticMsg = {
        id: tempId,
        temp_id: tempId,
        from_user_id: user.id,
        message: messageText,
        message_type: 'text',
        created_at: new Date().toISOString(),
        status: 'sending',
        reply_to: replyTo ? { id: replyTo.id, message: replyTo.message } : null
      };
      
      // INSTANT: Add to UI immediately (0ms)
      setMessages(prev => [...prev, optimisticMsg]);
      
      // Scroll to bottom immediately after sending (force scroll)
      requestAnimationFrame(() => {
        scrollToBottom(true);
      });
      
      // Store pending message for retry
      addPendingMessage({ ...optimisticMsg, conversation_id: conversationId });
      
      // Send via HTTP (simpler, avoids duplicate from socket broadcast)
      sendMessageHTTP(conversationId, messageText, tempId, replyTo?.id);
    }
  };
  
  // HTTP message sender (runs in background)
  const sendMessageHTTP = async (convId, message, tempId, replyToId) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('message', message);
      formData.append('temp_id', tempId);
      if (replyToId) formData.append('reply_to_id', replyToId);
      
      const response = await axios.post(`${API}/messages/conversation/${convId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Extract message from response (API returns { message: {...}, status: 'success' })
      const msgData = response.data.message || response.data;
      
      // Track message sent (check if first message for this user in this conversation)
      const isFirstMessage = messages.filter(m => m.from_user_id === user?.id).length === 0;
      trackMessageSent(isFirstMessage);
      
      // Update optimistic message with real data - replace temp_id message
      setMessages(prev => prev.map(m => {
        if (m.temp_id === tempId) {
          // Update optimistic message with real ID and data
          return { ...msgData, temp_id: tempId, status: 'sent' };
        }
        return m;
      }));
      
      // Remove from pending, add to cache
      removePendingMessage(tempId);
      addMessageToCache(convId, msgData);
      
    } catch (error) {
      console.error('[Chat] Send message error:', error);
      
      // Mark as failed (only if still in 'sending' status)
      setMessages(prev => prev.map(m => 
        (m.temp_id === tempId && m.status === 'sending') ? { ...m, status: 'failed' } : m
      ));
      
      // Keep in pending for retry
      toast.error('Message failed to send. Tap to retry.');
    }
  };
  
  // Retry failed message (text or media)
  const retryMessage = async (msg) => {
    console.log('[Retry] retryMessage called with:', { 
      temp_id: msg.temp_id, 
      status: msg.status, 
      message: msg.message,
      message_type: msg.message_type 
    });
    
    if (msg.status !== 'failed') {
      console.log('[Retry] Skipped - status is not failed:', msg.status);
      return;
    }
    
    // Check if it's a media message
    if (msg._mediaInfo || (msg.message_type && msg.message_type !== 'text')) {
      // Media message retry
      const mediaInfo = msg._mediaInfo || failedMediaMessage;
      
      if (!mediaInfo || !mediaInfo.file) {
        toast.error('Cannot retry - media file not available. Please select again.');
        // Remove the failed message
        setMessages(prev => prev.filter(m => m.temp_id !== msg.temp_id));
        return;
      }
      
      // Update status to sending
      setMessages(prev => prev.map(m => 
        m.temp_id === msg.temp_id ? { ...m, status: 'sending' } : m
      ));
      
      setIsUploading(true);
      
      try {
        const token = localStorage.getItem('token');
        
        const mediaFormData = new FormData();
        mediaFormData.append('file', mediaInfo.file);
        mediaFormData.append('conversation_id', conversationId);
        mediaFormData.append('view_once', mediaInfo.viewOnce);
        mediaFormData.append('view_once_duration', mediaInfo.viewOnceDuration);
        
        const uploadResponse = await axios.post(`${API}/media/upload`, mediaFormData, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 180000,
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });
        
        const mediaData = uploadResponse.data.media;
        
        const msgFormData = new FormData();
        msgFormData.append('media_url', mediaData.url);
        if (mediaData.thumbnail_url) msgFormData.append('thumbnail_url', mediaData.thumbnail_url);
        msgFormData.append('message_type', mediaData.media_type);
        msgFormData.append('caption', mediaInfo.caption || '');
        msgFormData.append('view_once', mediaInfo.viewOnce);
        msgFormData.append('view_once_duration', mediaInfo.viewOnceDuration);
        if (mediaData.dimensions) msgFormData.append('dimensions', JSON.stringify(mediaData.dimensions));
        if (mediaInfo.replyTo) msgFormData.append('reply_to_id', mediaInfo.replyTo.id);
        
        const msgResponse = await axios.post(`${API}/messages/conversation/${conversationId}/media`, msgFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Update with real message
        setMessages(prev => prev.map(m => 
          m.temp_id === msg.temp_id 
            ? { ...msgResponse.data.message, status: 'sent' }
            : m
        ));
        
        addMessageToCache(conversationId, msgResponse.data.message);
        setFailedMediaMessage(null);
        setSelectedMedia(null);
        setMediaPreview(null);
        setViewOnce(false);
        setViewOnceDuration(0);
        toast.success('Media sent successfully');
        
      } catch (error) {
        console.error('[Chat] Retry upload failed:', error);
        setMessages(prev => prev.map(m => 
          m.temp_id === msg.temp_id ? { ...m, status: 'failed' } : m
        ));
        toast.error('Retry failed. Please try again.');
      } finally {
        setUploadProgress(0);
        setIsUploading(false);
      }
      
    } else {
      // Text message retry
      console.log('[Retry] Retrying text message:', msg.temp_id, 'content:', msg.message);
      
      if (!msg.message) {
        console.error('[Retry] No message content found!');
        toast.error('Cannot retry - message content not found');
        return;
      }
      
      setMessages(prev => prev.map(m => 
        m.temp_id === msg.temp_id ? { ...m, status: 'sending' } : m
      ));
      
      sendMessageHTTP(conversationId, msg.message, msg.temp_id, msg.reply_to?.id);
    }
  };

  // Compress image on client side before upload (1280px max, WebP preferred)
  const compressImage = async (file, maxWidth = 1280, quality = 0.7) => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve(file);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        // Use document.createElement for explicit native Image element
        const img = document.createElement('img');
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          
          // Resize if needed (max 1280px on longest side)
          if (width > maxWidth || height > maxWidth) {
            if (width > height) {
              height = (height / width) * maxWidth;
              width = maxWidth;
            } else {
              width = (width / height) * maxWidth;
              height = maxWidth;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Try WebP first (25-40% smaller), fallback to JPEG
          const tryWebP = () => {
            canvas.toBlob(
              (blob) => {
                if (blob && blob.size > 0) {
                  // WebP supported
                  const ext = file.name.split('.').slice(0, -1).join('.') || 'image';
                  const compressedFile = new File([blob], `${ext}.webp`, {
                    type: 'image/webp',
                    lastModified: Date.now(),
                  });
                  console.log(`[Chat] Compressed image (WebP): ${(file.size/1024).toFixed(1)}KB -> ${(blob.size/1024).toFixed(1)}KB`);
                  resolve(compressedFile);
                } else {
                  // WebP not supported, fallback to JPEG
                  fallbackToJPEG();
                }
              },
              'image/webp',
              quality
            );
          };
          
          const fallbackToJPEG = () => {
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
                    type: 'image/jpeg',
                    lastModified: Date.now(),
                  });
                  console.log(`[Chat] Compressed image (JPEG fallback): ${(file.size/1024).toFixed(1)}KB -> ${(blob.size/1024).toFixed(1)}KB`);
                  resolve(compressedFile);
                } else {
                  resolve(file);
                }
              },
              'image/jpeg',
              quality
            );
          };
          
          // Start with WebP
          tryWebP();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Compress video on client side (limit to 720p max)
  const compressVideo = async (file) => {
    return new Promise((resolve) => {
      // Create video element to get dimensions
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        const { videoWidth, videoHeight } = video;
        URL.revokeObjectURL(video.src);
        
        // Check if video needs compression (> 720p)
        const maxDimension = Math.max(videoWidth, videoHeight);
        if (maxDimension <= 720) {
          console.log(`[Chat] Video already optimized: ${videoWidth}x${videoHeight}`);
          resolve(file);
          return;
        }
        
        // Calculate scale factor to fit within 720p
        const scale = 720 / maxDimension;
        const newWidth = Math.round(videoWidth * scale);
        const newHeight = Math.round(videoHeight * scale);
        
        console.log(`[Chat] Video compression needed: ${videoWidth}x${videoHeight} -> ${newWidth}x${newHeight}`);
        
        // Only show warning for LARGE videos (>5MB AND >1080p resolution)
        // This avoids showing warnings for small/short videos that don't need significant compression
        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > 5 && maxDimension > 1080) {
          toast.info(`Large video (${videoWidth}x${videoHeight}). Upload may take longer.`);
        }
        resolve(file);
      };
      
      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        resolve(file);
      };
      
      video.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 50MB');
      return;
    }
    
    let processedFile = file;
    
    // Compress images on client side before upload
    if (file.type.startsWith('image/') && file.size > 500 * 1024) {
      toast.info('Optimizing image...');
      processedFile = await compressImage(file);
    }
    
    // Check video dimensions (warn if > 720p)
    if (file.type.startsWith('video/')) {
      processedFile = await compressVideo(file);
    }
    
    setSelectedMedia(processedFile);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setMediaPreview({
        url: reader.result,
        type: file.type.startsWith('image/') ? 'image' : 
              file.type.startsWith('video/') ? 'video' : 'audio',
        name: file.name
      });
    };
    reader.readAsDataURL(processedFile);
    
    setShowMediaPicker(false);
  };

  const handleEmojiSelect = (emoji) => {
    setNewMessage(prev => prev + emoji);
    inputRef.current?.focus();
  };

  const addReaction = async (messageId, emoji) => {
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(`${API}/messages/message/${messageId}/react`, 
        { emoji },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchConversation();
      setShowReactions(null);
    } catch (error) {
      toast.error('Failed to add reaction');
    }
  };

  // Delete message functions
  const canDeleteForEveryone = (msg) => {
    // Can only delete for everyone if:
    // 1. Message is sent by current user
    // 2. Message is not yet read
    // 3. Message is within 1 hour of sending
    const userId = currentUserId || user?.id;
    if (msg.from_user_id !== userId) return false;
    if (msg.status === 'read') return false;
    
    const messageTime = new Date(msg.created_at);
    const now = new Date();
    const hourInMs = 60 * 60 * 1000;
    if (now - messageTime > hourInMs) return false;
    
    return true;
  };

  const deleteMessage = async (messageId, deleteType) => {
    executeDelete(async () => {
      setDeletingMessage(true);
      try {
        const token = localStorage.getItem('token');
        
        await axios.delete(`${API}/messages/message/${messageId}`, {
          headers: { Authorization: `Bearer ${token}` },
          data: { delete_type: deleteType }
        });
        
        if (deleteType === 'for_me') {
          // Remove from local state
          setMessages(prev => prev.filter(m => m.id !== messageId));
          toast.success('Message deleted');
        } else {
          // Mark as deleted for everyone in local state
          setMessages(prev => prev.map(m => 
            m.id === messageId 
              ? { ...m, deleted_for_everyone: true, deleted_for_everyone_at: new Date().toISOString() }
              : m
          ));
          toast.success('Message deleted for everyone');
        }
        
        setShowDeleteMenu(null);
      } catch (error) {
        const errMsg = error.response?.data?.detail || 'Failed to delete message';
        toast.error(errMsg);
      } finally {
        setDeletingMessage(false);
      }
    });
  };

  // Open full-screen media viewer (instead of opening in new tab)
  const openMediaViewer = (type, url) => {
    setMediaViewerContent({ type, url });
    setMediaViewerOpen(true);
  };

  // Close media viewer
  const closeMediaViewer = () => {
    setMediaViewerOpen(false);
    setMediaViewerContent(null);
  };

  // Cancel ongoing upload
  const cancelUpload = () => {
    if (uploadAbortControllerRef.current) {
      uploadAbortControllerRef.current.abort();
      uploadAbortControllerRef.current = null;
    }
    setIsUploading(false);
    setUploadProgress(0);
    setCanCancelUpload(false);
    setMediaPreview(null);
    setSelectedMedia(null);
    toast.info('Upload cancelled');
  };

  const handleViewOnceOpen = async (msg) => {
    // Check if already opened (check local state first, then msg state)
    if (msg.view_once_opened && msg.from_user_id !== user?.id) {
      toast.error('This media has already been viewed');
      return;
    }
    
    // Don't show viewer for sender
    if (msg.from_user_id === user?.id) {
      return;
    }
    
    setViewOnceMedia(msg);
    setShowViewOnceViewer(true);
    
    // Default to 5 seconds if no duration specified
    const duration = msg.view_once_duration > 0 ? msg.view_once_duration : 5;
    setViewOnceCountdown(duration);
    
    // Mark as opened in backend
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/media/view-once/${msg.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // IMPORTANT: Update local message state to prevent re-opening
      setMessages(prev => prev.map(m => 
        m.id === msg.id ? { ...m, view_once_opened: true, view_once_opened_at: new Date().toISOString() } : m
      ));
    } catch (error) {
      console.error('Failed to mark as viewed');
    }
    
    // Start countdown timer - always runs for view-once
    const interval = setInterval(() => {
      setViewOnceCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setShowViewOnceViewer(false);
          setViewOnceMedia(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ============================================
  // VOICE RECORDING - WHATSAPP STYLE (SIMPLIFIED)
  // ============================================
  
  // Get supported MIME type
  const getRecordingMimeType = () => {
    if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
      return 'audio/webm;codecs=opus';
    }
    if (MediaRecorder.isTypeSupported('audio/webm')) {
      return 'audio/webm';
    }
    if (MediaRecorder.isTypeSupported('audio/mp4')) {
      return 'audio/mp4';
    }
    if (MediaRecorder.isTypeSupported('audio/mpeg')) {
      return 'audio/mpeg';
    }
    return 'audio/webm';  // Default fallback
  };

  // START RECORDING
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,  // Lower sample rate for voice
        } 
      });
      recordingStreamRef.current = stream;
      
      const mimeType = getRecordingMimeType();
      console.log('[Voice] Using mimeType:', mimeType);
      
      // Use lower bitrate for smaller file sizes (~24kbps for voice is sufficient)
      const recorderOptions = { 
        mimeType,
        audioBitsPerSecond: 24000  // 24kbps - good quality for voice, small file size
      };
      
      mediaRecorderRef.current = new MediaRecorder(stream, recorderOptions);
      audioChunksRef.current = [];
      isCancelledRef.current = false;
      recordingSessionIdRef.current = crypto.randomUUID();
      recordingStartTimeRef.current = Date.now();
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        console.log('[Voice] Data available, size:', e.data.size);
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        console.log('[Voice] MediaRecorder stopped');
        
        // Stop all tracks
        if (recordingStreamRef.current) {
          recordingStreamRef.current.getTracks().forEach(track => track.stop());
        }
        
        // Calculate actual duration
        const actualDuration = recordingStartTimeRef.current 
          ? Math.floor((Date.now() - recordingStartTimeRef.current) / 1000)
          : 0;
        
        console.log('[Voice] Chunks collected:', audioChunksRef.current.length);
        console.log('[Voice] Duration:', actualDuration, 'seconds');
        
        // Skip if cancelled
        if (isCancelledRef.current) {
          console.log('[Voice] Recording was cancelled');
          audioChunksRef.current = [];
          return;
        }
        
        // Skip if no data
        if (audioChunksRef.current.length === 0) {
          console.log('[Voice] No audio chunks collected');
          toast.error('No audio recorded');
          return;
        }
        
        // Create blob
        const mimeType = getRecordingMimeType();
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        console.log('[Voice] Created blob, size:', blob.size);
        
        if (blob.size === 0) {
          console.log('[Voice] Blob is empty');
          toast.error('Recording failed - no audio data');
          return;
        }
        
        // Create file for upload
        const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
        const audioFile = new File([blob], `voice_note.${extension}`, { type: mimeType });
        
        // Upload directly using the media upload API
        try {
          toast.info('Sending voice message...');
          
          const token = localStorage.getItem('token');
          
          // Debug: Log blob info
          console.log('[Voice] Blob type:', blob.type);
          console.log('[Voice] Blob size:', blob.size);
          console.log('[Voice] AudioFile type:', audioFile.type);
          console.log('[Voice] AudioFile size:', audioFile.size);
          
          // Step 1: Upload the audio file
          const uploadFormData = new FormData();
          uploadFormData.append('file', audioFile);
          uploadFormData.append('conversation_id', conversationId);
          
          console.log('[Voice] Uploading to:', `${API}/media/upload`);
          
          const uploadResponse = await axios.post(
            `${API}/media/upload`,
            uploadFormData,
            { 
              headers: { 
                'Authorization': `Bearer ${token}`,
                // Don't set Content-Type - let browser set it with boundary
              },
              timeout: 60000  // 60 second timeout for slow connections
            }
          );
          
          console.log('[Voice] Upload response:', uploadResponse.data);
          
          if (!uploadResponse.data.media?.url) {
            console.error('[Voice] No URL in response:', uploadResponse.data);
            throw new Error('No URL in upload response');
          }
          
          const mediaUrl = uploadResponse.data.media.url;
          
          // Step 2: Create the message with the uploaded URL
          const messageFormData = new FormData();
          messageFormData.append('media_url', mediaUrl);
          messageFormData.append('message_type', 'audio');
          messageFormData.append('caption', '');
          messageFormData.append('duration', String(actualDuration));
          // For audio, view_once becomes listen_once on the backend
          messageFormData.append('view_once', listenOnce);
          
          console.log('[Voice] Creating message with URL:', mediaUrl, 'listen_once:', listenOnce);
          
          const messageResponse = await axios.post(
            `${API}/messages/conversation/${conversationId}/media`,
            messageFormData,
            { 
              headers: { 'Authorization': `Bearer ${token}` },
              timeout: 30000
            }
          );
          
          console.log('[Voice] Message response:', messageResponse.data);
          
          if (messageResponse.data.message) {
            // Only add to UI if not already added via socket
            setMessages(prev => {
              const exists = prev.some(m => m.id === messageResponse.data.message.id);
              if (exists) {
                // Message already added via socket, just update status
                return prev.map(m => 
                  m.id === messageResponse.data.message.id 
                    ? { ...m, status: 'sent' }
                    : m
                );
              }
              return [...prev, { ...messageResponse.data.message, status: 'sent' }];
            });
            addMessageToCache(conversationId, messageResponse.data.message);
            trackFeatureUsed('voice_message');
            const msg = listenOnce ? 'Listen-once voice message sent' : 'Voice message sent';
            toast.success(msg);
            setListenOnce(false); // Reset listen-once mode after sending
          } else {
            // No message in response but no error either - assume success
            toast.success('Voice message sent');
            setListenOnce(false);
          }
        } catch (error) {
          console.error('[Voice] Upload failed:', error);
          console.error('[Voice] Error response:', error.response?.data);
          console.error('[Voice] Error status:', error.response?.status);
          console.error('[Voice] Error message:', error.message);
          
          // Show more specific error
          if (error.response?.status === 413) {
            toast.error('Recording too large');
          } else if (error.response?.status === 400) {
            toast.error('Invalid recording format');
          } else if (error.code === 'ECONNABORTED') {
            toast.error('Upload timed out - check connection');
          } else {
            toast.error('Failed to send voice message');
          }
        }
      };
      
      // Start recording with timeslice to collect data periodically
      mediaRecorderRef.current.start(500);  // Collect data every 500ms
      setIsRecording(true);
      setRecordingDuration(0);
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      
      console.log('[Voice] Recording started');
      
    } catch (error) {
      console.error('[Voice] Failed to start recording:', error);
      toast.error('Failed to access microphone');
    }
  };

  // STOP RECORDING (SEND)
  const stopRecording = () => {
    if (!mediaRecorderRef.current || !isRecording) return;
    
    console.log('[Voice] Stopping recording to send');
    isCancelledRef.current = false;
    mediaRecorderRef.current.stop();
    setIsRecording(false);
    clearInterval(recordingIntervalRef.current);
  };

  // CANCEL RECORDING
  const cancelRecording = () => {
    if (!mediaRecorderRef.current || !isRecording) return;
    
    console.log('[Voice] Cancelling recording');
    isCancelledRef.current = true;
    mediaRecorderRef.current.stop();
    setIsRecording(false);
    clearInterval(recordingIntervalRef.current);
    audioChunksRef.current = [];
    
    toast.info('Recording cancelled');
  };

  const logout = () => {
    localStorage.removeItem('token');
    toast.success('Logged out');
    navigate('/login');
  };

  // ============================================
  // CLICK LOCK - Prevent double clicks
  // ============================================
  const { loading: callLoading, execute: executeCall } = useClickLock();
  const { loading: deleteLoading, execute: executeDelete } = useClickLock();

  const startCall = async (type) => {
    executeCall(async () => {
      // Pre-check media permissions before starting call
      try {
        const constraints = {
          audio: true,
          video: type === 'video'
        };
        
        // Request permission first
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        // Stop immediately - we just wanted to ensure permission
        stream.getTracks().forEach(track => track.stop());
        
        // Track feature usage
        trackFeatureUsed(type === 'video' ? 'video_call' : 'audio_call');
        
        // Now open the call interface
        setCallType(type);
        setIncomingCall(null);
        setShowCallInterface(true);
      } catch (error) {
        if (error.name === 'NotAllowedError') {
          toast.error('Please allow camera/microphone access in your browser settings to make calls');
        } else if (error.name === 'NotFoundError') {
          toast.error('No camera/microphone found on this device');
        } else {
          toast.error('Could not access camera/microphone');
        }
        console.error('[Chat] Media permission error:', error);
      }
    });
  };

  const getOtherUser = () => {
    // Use conversation if available, otherwise fall back to preloadedConv for instant display
    const conv = conversation || preloadedConv;
    const currentUser = user || { id: localStorage.getItem('userId') };
    
    if (!currentUser?.id || !conv) return { id: null, name: null, phone: '', username: '' };
    
    if (conv.user1_id === currentUser.id) {
      return { 
        id: conv.user2_id,
        name: conv.user2_name || conv.user2_phone,
        username: conv.user2_name,
        phone: conv.user2_phone 
      };
    }
    return { 
      id: conv.user1_id,
      name: conv.user1_name || conv.user1_phone,
      username: conv.user1_name,
      phone: conv.user1_phone 
    };
  };

  const otherUser = getOtherUser();

  // ============================================
  // MEMOIZED MESSAGE PROCESSING
  // Prevents re-computation on every render
  // ============================================
  
  // Filter out consecutive duplicate system messages (keeps the most recent one)
  const deduplicatedMessages = useMemo(() => {
    return messages.reduce((acc, msg, idx) => {
      // If this is a system message, check if the previous one is the same
      if (msg.message_type === 'system' || msg.from_user_id === 'system') {
        const lastMsg = acc[acc.length - 1];
        // If previous message is same system message, skip this one
        if (lastMsg && 
            (lastMsg.message_type === 'system' || lastMsg.from_user_id === 'system') &&
            lastMsg.message === msg.message) {
          return acc; // Skip duplicate
        }
      }
      acc.push(msg);
      return acc;
    }, []);
  }, [messages]);

  // Group messages by date - memoized
  const groupedMessages = useMemo(() => {
    return deduplicatedMessages.reduce((groups, msg) => {
      const date = new Date(msg.created_at).toDateString();
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
      return groups;
    }, {});
  }, [deduplicatedMessages]);

  // ============================================
  // RENDER MESSAGE - Memoized for performance
  // ============================================
  const renderMessage = useCallback((msg) => {
    const userId = currentUserId || user?.id;
    const isMine = userId && msg.from_user_id === userId;
    const isSystem = msg.message_type === 'system' || msg.from_user_id === 'system';
    const isMedia = ['image', 'video', 'audio'].includes(msg.message_type);
    const isViewOnce = msg.view_once;
    const isCallLog = msg.message_type === 'call_log';
    const isDeletedForEveryone = msg.deleted_for_everyone;
    
    // Check if message is expired
    if (msg.expires_at) {
      const expiryDate = new Date(msg.expires_at);
      if (new Date() > expiryDate) {
        // Don't render expired messages
        return null;
      }
    }
    
    // Handle "deleted for everyone" messages
    if (isDeletedForEveryone) {
      return (
        <div
          key={msg.id}
          className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
          data-testid={`message-${msg.id}`}
        >
          <div 
            className={`px-4 py-2 rounded-2xl ${isMine ? 'rounded-br-sm' : 'rounded-bl-sm'}`}
            style={{ 
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.05)'
            }}
          >
            <p className="text-sm italic" style={{ color: 'rgba(229,231,235,0.4)' }}>
              <X className="h-3 w-3 inline mr-1" />
              This message was deleted
            </p>
            <span className="text-[10px]" style={{ color: 'rgba(229,231,235,0.3)' }}>
              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
      );
    }
    
    // Handle call log messages
    if (isCallLog) {
      const isCalledByMe = msg.caller_id === userId;
      const callStatusText = {
        'missed': isCalledByMe ? 'Call not answered' : 'Missed call',
        'declined': isCalledByMe ? 'Call declined' : 'Declined call',
        'completed': `Call • ${msg.call_duration || 0}s`,
        'no_answer': isCalledByMe ? 'No answer' : 'Missed call'
      };
      const CallIcon = msg.call_type === 'video' ? Video : Phone;
      const isMissed = ['missed', 'no_answer', 'declined'].includes(msg.call_status);
      
      return (
        <div key={msg.id} className="flex justify-center my-3" data-testid={`call-log-${msg.id}`}>
          <div 
            className="px-4 py-2 rounded-full flex items-center gap-2 text-xs"
            style={{ 
              background: isMissed ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
              border: `1px solid ${isMissed ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.2)'}`
            }}
          >
            <CallIcon 
              className="h-3.5 w-3.5" 
              style={{ color: isMissed ? '#EF4444' : '#22C55E' }} 
            />
            <span style={{ color: isMissed ? '#FCA5A5' : '#86EFAC' }}>
              {callStatusText[msg.call_status] || 'Call'}
            </span>
            <span style={{ color: 'rgba(229,231,235,0.4)' }}>
              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
      );
    }

    if (isSystem) {
      return (
        <div key={msg.id} className="flex justify-center my-4" data-testid={`message-${msg.id}`}>
          <div 
            className="px-4 py-2 rounded-full flex items-center gap-2 text-xs"
            style={{ background: 'rgba(124, 58, 237, 0.15)', border: '1px solid rgba(124, 58, 237, 0.2)' }}
          >
            <Settings className="h-3 w-3" style={{ color: '#A78BFA' }} />
            <span style={{ color: 'rgba(229, 231, 235, 0.7)' }}>{msg.message}</span>
          </div>
        </div>
      );
    }

    // Swipe gesture handlers for this message
    const handleTouchStart = (e) => {
      if (isSystem || isCallLog || isDeletedForEveryone) return;
      const touch = e.touches[0];
      swipeStartRef.current = { x: touch.clientX, y: touch.clientY };
      isSwipingRef.current = false;
      setSwipingMessageId(msg.id);
      setSwipeOffset(0);
    };
    
    const handleTouchMove = (e) => {
      if (swipingMessageId !== msg.id) return;
      const touch = e.touches[0];
      const deltaX = touch.clientX - swipeStartRef.current.x;
      const deltaY = touch.clientY - swipeStartRef.current.y;
      
      // Check if horizontal swipe (more horizontal than vertical)
      if (Math.abs(deltaX) > Math.abs(deltaY) * 1.5 && Math.abs(deltaX) > 10) {
        isSwipingRef.current = true;
        
        // For my messages (right side), swipe LEFT to reply (negative)
        // For their messages (left side), swipe RIGHT to reply (positive)
        const direction = isMine ? -1 : 1;
        const swipeAmount = deltaX * direction;
        
        // Only allow swipe in the correct direction and cap it
        if (swipeAmount > 0) {
          setSwipeOffset(Math.min(swipeAmount, SWIPE_THRESHOLD + 20));
          e.preventDefault(); // Prevent scroll while swiping
        }
      }
    };
    
    const handleTouchEnd = () => {
      if (swipingMessageId !== msg.id) return;
      
      // If swiped past threshold, trigger reply
      if (swipeOffset >= SWIPE_THRESHOLD) {
        setReplyingTo(msg);
        // Haptic feedback
        if (navigator.vibrate) navigator.vibrate(30);
      }
      
      // Reset swipe state
      setSwipeOffset(0);
      setSwipingMessageId(null);
      isSwipingRef.current = false;
    };
    
    // Calculate swipe transform
    const swipeTransform = swipingMessageId === msg.id && swipeOffset > 0
      ? `translateX(${isMine ? -swipeOffset : swipeOffset}px)`
      : 'translateX(0)';
    
    const isSwipeActive = swipingMessageId === msg.id && swipeOffset > 0;
    const swipeProgress = Math.min(swipeOffset / SWIPE_THRESHOLD, 1);

    return (
      <div
        key={msg.id}
        className={`flex ${isMine ? 'justify-end' : 'justify-start'} group relative`}
        data-testid={`message-${msg.id}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Reply indicator icon - shows during swipe */}
        {isSwipeActive && (
          <div 
            className={`absolute top-1/2 -translate-y-1/2 ${isMine ? 'right-full mr-2' : 'left-full ml-2'} transition-opacity`}
            style={{ 
              opacity: swipeProgress,
              transform: `scale(${0.5 + swipeProgress * 0.5})`
            }}
          >
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ 
                background: swipeProgress >= 1 ? 'rgba(124, 58, 237, 0.3)' : 'rgba(255,255,255,0.1)',
                border: swipeProgress >= 1 ? '2px solid #7C3AED' : '1px solid rgba(255,255,255,0.2)'
              }}
            >
              <Reply className="h-4 w-4" style={{ color: swipeProgress >= 1 ? '#A78BFA' : 'rgba(255,255,255,0.5)' }} />
            </div>
          </div>
        )}
        
        <div 
          className="relative max-w-[85%] sm:max-w-[75%] lg:max-w-md"
          style={{
            transform: swipeTransform,
            transition: swipingMessageId === msg.id ? 'none' : 'transform 0.2s ease-out'
          }}
        >
          {/* Reply preview */}
          {msg.reply_to_preview && (
            <div 
              className={`text-xs px-3 py-1.5 rounded-t-xl mb-0.5 ${isMine ? 'rounded-br-sm' : 'rounded-bl-sm'}`}
              style={{ 
                background: isMine ? 'rgba(124, 58, 237, 0.3)' : 'rgba(255,255,255,0.08)',
                borderLeft: '2px solid #A78BFA'
              }}
            >
              <Reply className="h-3 w-3 inline mr-1" style={{ color: '#A78BFA' }} />
              <span style={{ color: 'rgba(229,231,235,0.6)' }}>{msg.reply_to_preview}</span>
            </div>
          )}
          
          {/* Message bubble */}
          <div
            className={`relative px-3 sm:px-4 py-2 sm:py-3 rounded-2xl ${
              isMine ? 'rounded-br-sm' : 'rounded-bl-sm'
            }`}
            style={isMine ? {
              background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)'
            } : {
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.08)'
            }}
            onDoubleClick={() => setShowReactions(msg.id)}
          >
            {/* Bubble tail */}
            <div 
              className={`absolute bottom-0 w-3 h-3 ${isMine ? '-right-1' : '-left-1'}`}
              style={{
                background: isMine ? '#5B21B6' : 'rgba(255, 255, 255, 0.05)',
                clipPath: isMine ? 'polygon(0 0, 100% 100%, 0 100%)' : 'polygon(100% 0, 100% 100%, 0 100%)'
              }}
            />
            
            {/* View-once media */}
            {isMedia && isViewOnce ? (
              <div 
                className={isMine ? '' : 'cursor-pointer active:scale-95 transition-transform'}
                onClick={() => !isMine && handleViewOnceOpen(msg)}
                data-testid={`view-once-${msg.id}`}
              >
                {/* Sender view - can't open their own view-once */}
                {isMine ? (
                  <div className="flex items-center gap-2 py-4 px-6 rounded-xl" style={{ background: 'rgba(245,158,11,0.15)' }}>
                    <Flame className="h-5 w-5 text-orange-400" />
                    <span className="text-sm text-orange-300">
                      {msg.message_type === 'image' ? 'Photo' : msg.message_type === 'video' ? 'Video' : 'Voice note'}
                    </span>
                    {msg.view_once_opened ? (
                      <span className="text-xs text-green-400 ml-2">• Viewed</span>
                    ) : (
                      <span className="text-xs text-orange-400/60 ml-2">• View once</span>
                    )}
                  </div>
                ) : msg.view_once_opened ? (
                  /* Receiver already opened */
                  <div className="flex items-center gap-2 py-4 px-6 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)' }}>
                    <Flame className="h-5 w-5 text-orange-400" />
                    <span className="text-sm" style={{ color: 'rgba(229,231,235,0.6)' }}>Opened</span>
                  </div>
                ) : (
                  /* Receiver can tap to view */
                  <div className="flex items-center gap-2 py-4 px-6 rounded-xl hover:bg-orange-500/30 transition-colors" style={{ background: 'rgba(245,158,11,0.2)' }}>
                    <Flame className="h-5 w-5 text-orange-400 animate-pulse" />
                    <span className="text-sm text-orange-300 font-medium">
                      Tap to view {msg.message_type === 'image' ? 'photo' : msg.message_type === 'video' ? 'video' : 'voice note'}
                    </span>
                    {msg.view_once_duration > 0 && (
                      <span className="text-xs text-orange-400">{msg.view_once_duration}s</span>
                    )}
                  </div>
                )}
              </div>
            ) : isMedia ? (
              // Regular media - check if expired first
              <MediaMessage msg={msg} isMine={isMine} onMediaPlayed={markMediaPlayed} onOpenViewer={openMediaViewer} />
            ) : null}
            
            {/* Text content - show message or placeholder for empty messages */}
            {msg.message ? (
              <p className="text-sm break-words" style={{ color: isMine ? 'white' : '#E5E7EB' }}>
                {msg.message}
              </p>
            ) : !isMedia && !isCallLog && (
              <p className="text-sm italic" style={{ color: isMine ? 'rgba(255,255,255,0.5)' : 'rgba(229,231,235,0.4)' }}>
                (No content)
              </p>
            )}
            
            {/* Timestamp and status */}
            <div className="flex items-center justify-between gap-2 mt-1">
              <p 
                className="text-xs"
                style={{ color: isMine ? 'rgba(255, 255, 255, 0.6)' : 'rgba(229, 231, 235, 0.4)' }}
              >
                {new Date(msg.created_at).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
              <div className="flex items-center gap-1">
                {msg.expires_at && (
                  <Timer className="h-3 w-3" style={{ color: isMine ? 'rgba(255,255,255,0.5)' : 'rgba(229, 231, 235, 0.4)' }} />
                )}
                {/* Message status - WhatsApp style with read timestamps */}
                {isMine && (
                  <MessageStatus 
                    status={msg.status || (msg.read_at ? 'read' : msg.delivered_at ? 'delivered' : 'sent')} 
                    readAt={msg.read_at}
                    deliveredAt={msg.delivered_at}
                  />
                )}
                {/* Tap to retry for failed messages */}
                {msg.status === 'failed' && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      console.log('[Retry] Clicked for message:', msg.temp_id, 'text:', msg.message);
                      retryMessage(msg);
                    }}
                    className="text-xs text-red-400 hover:text-red-300 ml-1 px-2 py-1 bg-red-500/20 rounded"
                    data-testid="retry-message-btn"
                  >
                    ↻ Retry
                  </button>
                )}
              </div>
            </div>
            
            {/* Reactions */}
            {msg.reactions && Object.keys(msg.reactions).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {Object.entries(msg.reactions).map(([emoji, users]) => (
                  <button
                    key={emoji}
                    onClick={() => addReaction(msg.id, emoji)}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-all hover:scale-110"
                    style={{ background: 'rgba(255,255,255,0.1)' }}
                  >
                    <span>{emoji}</span>
                    <span style={{ color: 'rgba(229,231,235,0.6)' }}>{users.length}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Message actions - Desktop: hover, Mobile: tap on message bubble */}
          <div className={`absolute ${isMine ? '-left-20' : '-right-20'} top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity gap-1 hidden sm:flex`}>
            <button
              onClick={() => setReplyingTo(msg)}
              className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
              title="Reply"
            >
              <Reply className="h-4 w-4 text-gray-400" />
            </button>
            <button
              onClick={() => setShowReactions(msg.id === showReactions ? null : msg.id)}
              className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
              title="React"
            >
              <Heart className="h-4 w-4 text-gray-400" />
            </button>
            <button
              onClick={() => setShowDeleteMenu(showDeleteMenu === msg.id ? null : msg.id)}
              className="p-1.5 rounded-full hover:bg-red-500/20 transition-colors"
              title="Delete"
            >
              <Trash2 className="h-4 w-4 text-red-400" />
            </button>
          </div>
          
          {/* Mobile: Show delete/actions menu on LONG PRESS only - but NOT for audio or view-once messages */}
          {showDeleteMenu !== msg.id && msg.message_type !== 'audio' && !msg.view_once && (
            <div 
              className="absolute inset-0 sm:hidden"
              onTouchStart={(e) => {
                // Start long press timer
                const timer = setTimeout(() => {
                  setShowDeleteMenu(msg.id);
                  // Vibrate if supported (haptic feedback)
                  if (navigator.vibrate) navigator.vibrate(50);
                }, 500); // 500ms long press
                e.currentTarget.dataset.longPressTimer = timer;
              }}
              onTouchEnd={(e) => {
                // Cancel long press if touch ends before timer
                const timer = e.currentTarget.dataset.longPressTimer;
                if (timer) clearTimeout(parseInt(timer));
              }}
              onTouchMove={(e) => {
                // Cancel long press if user moves finger
                const timer = e.currentTarget.dataset.longPressTimer;
                if (timer) clearTimeout(parseInt(timer));
              }}
            />
          )}
          
          {/* Delete menu */}
          {showDeleteMenu === msg.id && (
            <div 
              className={`absolute ${isMine ? 'right-0' : 'left-0'} -top-24 flex flex-col gap-1 p-2 rounded-xl z-30`}
              style={{ background: 'rgba(10,15,44,0.98)', border: '1px solid rgba(255,255,255,0.1)', minWidth: '160px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteMessage(msg.id, 'for_me');
                }}
                disabled={deletingMessage || deleteLoading}
                className="flex items-center gap-2 px-3 py-2 text-sm text-left rounded-lg hover:bg-white/10 transition-colors w-full"
                style={{ color: 'rgba(229,231,235,0.8)' }}
              >
                {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete for me
              </button>
              {canDeleteForEveryone(msg) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteMessage(msg.id, 'for_everyone');
                  }}
                  disabled={deletingMessage || deleteLoading}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-left rounded-lg hover:bg-red-500/20 transition-colors text-red-400 w-full"
                >
                  {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  Delete for everyone
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteMenu(null);
                }}
                className="flex items-center gap-2 px-3 py-2 text-sm text-left rounded-lg hover:bg-white/10 transition-colors w-full"
                style={{ color: 'rgba(229,231,235,0.5)' }}
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
            </div>
          )}
          
          {/* Reaction picker */}
          {showReactions === msg.id && (
            <div 
              className={`absolute ${isMine ? 'right-0' : 'left-0'} -top-12 flex gap-1 p-2 rounded-full z-10`}
              style={{ background: 'rgba(10,15,44,0.95)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              {REACTION_EMOJIS.map(emoji => (
                <button
                  key={emoji}
                  onClick={() => addReaction(msg.id, emoji)}
                  className="text-xl hover:scale-125 transition-transform p-1"
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }, [currentUserId, user?.id, showDeleteMenu, deletingMessage, showReactions, swipingMessageId, swipeOffset]);

  return (
    <div 
      className="flex flex-col h-full" 
      style={{ 
        background: '#020617',
        height: 'var(--app-height, 100dvh)', // Use CSS variable for iOS Chrome, fallback to 100dvh
        overflow: 'hidden',
      }} 
      data-testid="chat-page"
    >
      {/* Header */}
      <header 
        className="shrink-0 z-10 border-b safe-area-top"
        style={{ 
          background: 'rgba(10, 15, 44, 0.95)',
          backdropFilter: 'blur(20px)',
          borderColor: 'rgba(255, 255, 255, 0.05)'
        }}
      >
        <div className="max-w-4xl mx-auto px-2 sm:px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleBack}
              className="text-gray-400 hover:text-white hover:bg-white/10 shrink-0"
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            
            <div className="relative w-9 h-9 sm:w-10 sm:h-10 shrink-0">
              {conversation?.other_user_profile_picture ? (
                <img 
                  src={conversation.other_user_profile_picture} 
                  alt={otherUser.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <div className="w-full h-full rounded-full flex items-center justify-center" 
                     style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}>
                  <User className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
              )}
              {/* Online indicator */}
              {otherUserOnline && (
                <div 
                  className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-500 border-2" 
                  style={{ borderColor: 'rgba(10, 15, 44, 0.95)' }}
                />
              )}
            </div>
            
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {otherUser.name ? (
                  <h2 className="text-base sm:text-lg font-semibold truncate" style={{ color: '#E5E7EB' }}>
                    {otherUser.name}
                  </h2>
                ) : (
                  <div className="h-5 w-24 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.1)' }} />
                )}
                {conversation?.is_guest_chat && !conversation?.other_user_converted && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full shrink-0" style={{ background: 'rgba(34, 211, 238, 0.2)', color: '#22D3EE' }}>
                    Guest
                  </span>
                )}
                {conversation?.other_user_converted && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full shrink-0" style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22C55E' }}>
                    Joined
                  </span>
                )}
              </div>
              {otherUser.name ? (
                typingUsers.length > 0 ? (
                  <p className="text-xs truncate" style={{ color: '#22D3EE' }}>
                    typing...
                  </p>
                ) : otherUserOnline ? (
                  <p className="text-xs truncate text-green-400">
                    online
                  </p>
                ) : otherUserLastSeen ? (
                  <p className="text-xs truncate" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
                    last seen {formatLastSeen(otherUserLastSeen)}
                  </p>
                ) : conversation?.is_guest_chat ? (
                  <p className="text-xs truncate" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
                    Invited via link
                  </p>
                ) : (
                  <p className="text-xs truncate" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
                    {conversation?.disappearing_enabled ? `Messages disappear after ${getTimerDisplay(disappearingTimer)}` : 'Tap to view profile'}
                  </p>
                )
              ) : (
                <div className="h-3 w-16 rounded animate-pulse mt-1" style={{ background: 'rgba(255,255,255,0.05)' }} />
              )}
            </div>
          </div>

          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => startCall('audio')}
              disabled={callLoading}
              className="text-gray-400 hover:text-green-400 hover:bg-green-400/10"
              data-testid="call-audio-btn"
            >
              {callLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Phone className="h-5 w-5" />}
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => startCall('video')}
              disabled={callLoading}
              className="text-gray-400 hover:text-blue-400 hover:bg-blue-400/10"
              data-testid="call-video-btn"
            >
              {callLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Video className="h-5 w-5" />}
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white hover:bg-white/10">
                  <MoreVertical className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-slate-900 border-slate-800">
                <DropdownMenuItem onClick={() => setShowDisappearingSettings(true)} className="text-gray-300 focus:text-white focus:bg-white/10">
                  <Timer className="h-4 w-4 mr-2" /> Disappearing messages
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-800" />
                <DropdownMenuItem 
                  onClick={async () => {
                    if (window.confirm('Clear all messages in this chat? This only removes them from your view.')) {
                      try {
                        const token = localStorage.getItem('token');
                        // Use POST /clear endpoint - only clears messages, keeps conversation
                        await axios.post(`${API}/messages/conversation/${conversationId}/clear`, {}, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        setMessages([]);
                        toast.success('Chat cleared');
                      } catch (error) {
                        toast.error('Failed to clear chat');
                      }
                    }
                  }} 
                  className="text-red-400 focus:text-red-300 focus:bg-red-500/10"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Clear chat
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-800" />
                <DropdownMenuItem onClick={logout} className="text-gray-300 focus:text-white focus:bg-white/10">
                  <LogOut className="h-4 w-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Messages - SINGLE SCROLL CONTAINER */}
      <main 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        onClick={() => {
          // Close delete menu when clicking outside
          if (showDeleteMenu) setShowDeleteMenu(null);
          if (showReactions) setShowReactions(null);
        }}
        className="flex-1 overflow-y-auto px-3 sm:px-4 py-4 sm:py-6"
        style={{ 
          background: 'linear-gradient(180deg, #0A0F2C 0%, #020617 100%)',
          minHeight: 0, // Critical for flex-1 to shrink properly
          WebkitOverflowScrolling: 'touch',
        }}
        data-testid="messages-container"
      >
        <div className="max-w-4xl mx-auto space-y-3 sm:space-y-4">
          {/* Loading more indicator */}
          {loadingMore && (
            <div className="flex justify-center py-2">
              <div className="px-4 py-2 rounded-full text-xs flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <div className="w-3 h-3 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
                <span style={{ color: 'rgba(229,231,235,0.5)' }}>Loading older messages...</span>
              </div>
            </div>
          )}
          
          {/* Load more button for manual trigger */}
          {hasMoreMessages && !loadingMore && messages.length > 0 && (
            <div className="flex justify-center py-2">
              <button
                onClick={loadMoreMessages}
                className="px-4 py-2 rounded-full text-xs transition-all hover:scale-105"
                style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)', color: 'rgba(229,231,235,0.6)' }}
              >
                Load older messages
              </button>
            </div>
          )}
          
          {/* Disappearing Messages Notice - Inside chat */}
          {conversation && (
            <div className="flex justify-center my-2">
              <button 
                onClick={() => setShowDisappearingSettings(true)}
                className="px-4 py-2 rounded-full flex items-center gap-2 text-xs transition-all hover:scale-105"
                style={{ background: 'rgba(124, 58, 237, 0.15)', border: '1px solid rgba(124, 58, 237, 0.2)' }}
              >
                <Shield className="h-3.5 w-3.5" style={{ color: '#A78BFA' }} />
                <span style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
                  {disappearingTimer === 'off' 
                    ? 'Disappearing messages off'
                    : `Messages disappear after ${getTimerDisplay(disappearingTimer)}`}
                </span>
                <Timer className="h-3 w-3" style={{ color: '#A78BFA' }} />
              </button>
            </div>
          )}
          
          {initialLoading ? (
            // Show loading skeleton while fetching initial messages
            <div className="space-y-4 py-4" data-testid="messages-loading">
              {/* Skeleton messages - alternating left and right */}
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                  <div 
                    className="rounded-2xl animate-pulse"
                    style={{ 
                      background: i % 2 === 0 ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255,255,255,0.05)',
                      width: `${120 + (i * 20)}px`,
                      height: '40px'
                    }}
                  />
                </div>
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12" data-testid="no-messages">
              <Lock className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-4" style={{ color: 'rgba(124, 58, 237, 0.3)' }} />
              <p className="text-sm sm:text-base mb-2" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
                Messages are end-to-end encrypted.
              </p>
              <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.3)' }}>
                Start the conversation!
              </p>
            </div>
          ) : (
            Object.entries(groupedMessages).map(([date, msgs]) => (
              <div key={date}>
                {/* Date separator */}
                <div className="flex items-center justify-center my-4">
                  <div className="px-3 py-1 rounded-full text-xs" style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(229,231,235,0.5)' }}>
                    {formatDateSeparator(date)}
                  </div>
                </div>
                {/* Messages for this date */}
                <div className="space-y-3">
                  {msgs.map(renderMessage)}
                </div>
              </div>
            ))
          )}
          
          {/* Typing indicator */}
          {typingUsers.length > 0 && (
            <div className="flex justify-start">
              <div className="px-4 py-3 rounded-2xl rounded-bl-sm" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <div className="flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Scroll to bottom button with unread count */}
      {showScrollButton && (
        <button
          onClick={() => {
            scrollToBottom(true, true);
            setUnreadCount(0);
          }}
          className="fixed bottom-20 right-4 sm:right-8 z-30 flex items-center justify-center shadow-lg transition-all hover:scale-110 active:scale-95"
          style={{ 
            background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
            boxShadow: '0 4px 15px rgba(124,58,237,0.4)',
            minWidth: '40px',
            height: '40px',
            borderRadius: unreadCount > 0 ? '20px' : '50%',
            padding: unreadCount > 0 ? '0 12px' : '0'
          }}
          data-testid="scroll-to-bottom-btn"
        >
          {unreadCount > 0 ? (
            <span className="flex items-center gap-1.5 text-white text-sm font-medium">
              <ChevronDown className="h-4 w-4" />
              <span>{unreadCount > 99 ? '99+' : unreadCount}</span>
            </span>
          ) : (
            <ChevronDown className="h-5 w-5 text-white" />
          )}
        </button>
      )}

      {/* Reply bar */}
      {replyingTo && (
        <div 
          className="px-4 py-2 flex items-center justify-between"
          style={{ background: 'rgba(124,58,237,0.1)', borderTop: '1px solid rgba(124,58,237,0.2)' }}
        >
          <div className="flex items-center gap-2">
            <Reply className="h-4 w-4" style={{ color: '#A78BFA' }} />
            <span className="text-sm" style={{ color: 'rgba(229,231,235,0.7)' }}>
              Replying to: {replyingTo.message?.slice(0, 30)}...
            </span>
          </div>
          <button onClick={() => setReplyingTo(null)} className="p-1 hover:bg-white/10 rounded">
            <X className="h-4 w-4 text-gray-400" />
          </button>
        </div>
      )}

      {/* Media preview */}
      {mediaPreview && (
        <div 
          className="shrink-0 px-4 py-3"
          style={{ background: 'rgba(10,15,44,0.95)', borderTop: '1px solid rgba(255,255,255,0.1)' }}
        >
          <div className="flex items-start gap-3">
            <div className="relative">
              {mediaPreview.type === 'image' && (
                <img src={mediaPreview.url} alt="Preview" className="h-20 w-20 object-cover rounded-lg" />
              )}
              {mediaPreview.type === 'video' && (
                <video src={mediaPreview.url} className="h-20 w-20 object-cover rounded-lg" />
              )}
              {mediaPreview.type === 'audio' && (
                <div className="h-20 w-20 rounded-lg flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.2)' }}>
                  <Volume2 className="h-8 w-8 text-purple-400" />
                </div>
              )}
              {!isUploading && (
                <button 
                  onClick={() => { setSelectedMedia(null); setMediaPreview(null); setViewOnce(false); setViewOnceDuration(0); }}
                  className="absolute -top-2 -right-2 p-1 rounded-full bg-red-500 hover:bg-red-600"
                >
                  <X className="h-3 w-3 text-white" />
                </button>
              )}
            </div>
            
            {/* View once toggle - for media (image/video) or Listen once (audio) */}
            {!isUploading && (
              <div className="flex-1 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={viewOnce}
                    onChange={(e) => setViewOnce(e.target.checked)}
                    className="w-4 h-4 rounded"
                  />
                  <Flame className="h-4 w-4 text-orange-400" />
                  <span className="text-sm" style={{ color: '#E5E7EB' }}>
                    {mediaPreview?.type === 'audio' ? 'Listen once' : 'View once'}
                  </span>
                </label>
                
                {viewOnce && (
                  <p className="text-xs text-gray-400 pl-6">
                    {mediaPreview?.type === 'audio' 
                      ? 'Disappears after playback'
                      : 'Disappears after viewing'}
                  </p>
                )}
              </div>
            )}
            
            {/* Show uploading status with cancel button */}
            {isUploading && (
              <div className="flex-1 flex items-center gap-2">
                <Loader2 className="h-4 w-4 text-purple-400 animate-spin" />
                <span className="text-sm text-gray-400">Uploading... {uploadProgress > 0 ? `${uploadProgress}%` : ''}</span>
                <button
                  onClick={cancelUpload}
                  className="ml-2 px-2 py-1 text-xs rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          
          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${uploadProgress}%`, background: 'linear-gradient(90deg, #7C3AED, #22D3EE)' }} />
            </div>
          )}
        </div>
      )}

      {/* Message input */}
      <footer 
        className="shrink-0 border-t pb-safe-safari"
        style={{ 
          background: 'rgba(10, 15, 44, 0.98)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderColor: 'rgba(255, 255, 255, 0.05)',
        }}
        data-testid="chat-footer"
      >
        <div className="max-w-4xl mx-auto px-2 sm:px-4 py-3">
          {isRecording ? (
            // Recording UI - WhatsApp Style
            <div className="flex items-center gap-3">
              {/* Cancel button (slide left to cancel) */}
              <button 
                onClick={cancelRecording} 
                className="p-2 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                title="Cancel recording"
              >
                <X className="h-5 w-5" />
              </button>
              
              {/* Recording indicator */}
              <div className="flex-1 flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                <span className="text-sm font-medium" style={{ color: '#E5E7EB' }}>
                  {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}
                </span>
                <span className="text-xs text-gray-400">Recording...</span>
                <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                  <div 
                    className="h-full bg-gradient-to-r from-red-500 to-red-400 animate-pulse" 
                    style={{ width: `${Math.min(100, recordingDuration * 2)}%` }} 
                  />
                </div>
              </div>
              
              {/* Listen-once toggle */}
              <button
                type="button"
                onClick={() => setListenOnce(!listenOnce)}
                className={`p-2 rounded-full transition-all ${listenOnce ? 'bg-orange-500/30' : 'bg-white/5 hover:bg-white/10'}`}
                title={listenOnce ? 'Listen once ON - will disappear after playback' : 'Listen once OFF'}
                data-testid="listen-once-toggle"
              >
                <Flame className={`h-5 w-5 ${listenOnce ? 'text-orange-400 animate-pulse' : 'text-gray-400'}`} />
              </button>
              
              {/* Send button */}
              <button 
                onClick={stopRecording} 
                className="p-3 rounded-full shadow-lg hover:scale-105 transition-transform" 
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                title={listenOnce ? 'Send listen-once voice message' : 'Send voice message'}
              >
                <Send className="h-5 w-5 text-white" />
              </button>
            </div>
          ) : (
            // Normal input UI
            <form onSubmit={sendMessage} className="flex gap-2 items-center" data-testid="message-form">
              {/* Attachment button */}
              <Popover open={showMediaPicker} onOpenChange={setShowMediaPicker}>
                <PopoverTrigger asChild>
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="icon"
                    className="text-gray-400 hover:text-white hover:bg-white/10 shrink-0"
                  >
                    <Paperclip className="h-5 w-5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  className="w-48 p-2"
                  style={{ background: 'rgba(10,15,44,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <div className="space-y-1">
                    <button
                      type="button"
                      onClick={() => { fileInputRef.current?.click(); setShowMediaPicker(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <ImageIcon className="h-5 w-5 text-purple-400" />
                      <span className="text-sm" style={{ color: '#E5E7EB' }}>Photo</span>
                    </button>
                    {/* Video upload temporarily disabled
                    <button
                      type="button"
                      onClick={() => { videoInputRef.current?.click(); setShowMediaPicker(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <Video className="h-5 w-5 text-blue-400" />
                      <span className="text-sm" style={{ color: '#E5E7EB' }}>Video</span>
                    </button>
                    */}
                    <button
                      type="button"
                      onClick={() => { setShowMediaPicker(false); startRecording(); }}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <Mic className="h-5 w-5 text-green-400" />
                      <span className="text-sm" style={{ color: '#E5E7EB' }}>Voice note</span>
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
              
              <input 
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <input 
                ref={videoInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {/* Timer button for quick access to disappearing settings */}
              <Button 
                type="button"
                variant="ghost" 
                size="icon"
                onClick={() => setShowDisappearingSettings(true)}
                className="text-gray-400 hover:text-purple-400 hover:bg-purple-400/10 shrink-0"
                title="Disappearing messages"
              >
                <Timer className="h-5 w-5" />
              </Button>
              
              {/* Emoji picker */}
              <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                <PopoverTrigger asChild>
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="icon"
                    className="text-gray-400 hover:text-white hover:bg-white/10 shrink-0"
                  >
                    <Smile className="h-5 w-5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  className="w-72 p-3"
                  style={{ background: 'rgba(10,15,44,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  {/* Category tabs */}
                  <div className="flex gap-1 mb-3 overflow-x-auto pb-2">
                    {Object.keys(EMOJI_CATEGORIES).map(cat => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setEmojiCategory(cat)}
                        className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition-all ${emojiCategory === cat ? 'bg-purple-500 text-white' : 'bg-white/10 text-gray-300'}`}
                      >
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </button>
                    ))}
                  </div>
                  {/* Emoji grid */}
                  <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto">
                    {EMOJI_CATEGORIES[emojiCategory].map((emoji, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => { handleEmojiSelect(emoji); setShowEmojiPicker(false); }}
                        className="text-xl p-1 hover:bg-white/10 rounded transition-all hover:scale-110"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              
              <Input
                ref={inputRef}
                value={newMessage}
                onChange={handleInputChange}
                placeholder="Type a message..."
                className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-purple-500 h-11"
                disabled={loading}
                data-testid="message-input"
              />
              
              {newMessage.trim() || selectedMedia ? (
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="text-white p-3 h-11 w-11 shrink-0"
                  style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                  data-testid="send-button"
                >
                  <Send className="h-4 w-4" />
                </Button>
              ) : (
                <Button 
                  type="button"
                  onClick={startRecording}
                  className="text-white p-3 h-11 w-11 shrink-0"
                  style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                >
                  <Mic className="h-4 w-4" />
                </Button>
              )}
            </form>
          )}
        </div>
      </footer>

      {/* View Once Viewer Modal */}
      <Dialog open={showViewOnceViewer} onOpenChange={(open) => {
        if (!open) {
          setShowViewOnceViewer(false);
          setViewOnceMedia(null);
        }
      }}>
        <DialogContent 
          className="border-0 max-w-lg p-0 overflow-hidden"
          style={{ background: 'black' }}
        >
          <div className="relative">
            {viewOnceMedia?.message_type === 'image' && viewOnceMedia?.media_urls?.[0] && (
              <img 
                src={`${process.env.REACT_APP_BACKEND_URL}${viewOnceMedia.media_urls[0]}${viewOnceMedia.media_urls[0].includes('?') ? '&' : '?'}token=${localStorage.getItem('token')}`}
                alt="View once"
                className="w-full h-auto max-h-[80vh] object-contain"
              />
            )}
            {viewOnceMedia?.message_type === 'video' && viewOnceMedia?.media_urls?.[0] && (
              <video 
                src={`${process.env.REACT_APP_BACKEND_URL}${viewOnceMedia.media_urls[0]}${viewOnceMedia.media_urls[0].includes('?') ? '&' : '?'}token=${localStorage.getItem('token')}`}
                autoPlay
                className="w-full h-auto max-h-[80vh]"
              />
            )}
            {viewOnceMedia?.message_type === 'audio' && viewOnceMedia?.media_urls?.[0] && (
              <div className="p-8 flex flex-col items-center justify-center">
                <Volume2 className="h-16 w-16 text-purple-400 mb-4" />
                <audio 
                  src={`${process.env.REACT_APP_BACKEND_URL}${viewOnceMedia.media_urls[0]}${viewOnceMedia.media_urls[0].includes('?') ? '&' : '?'}token=${localStorage.getItem('token')}`}
                  autoPlay
                  controls
                  className="w-full"
                />
              </div>
            )}
            
            {/* Countdown overlay */}
            {viewOnceCountdown > 0 && (
              <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full flex items-center gap-2" style={{ background: 'rgba(0,0,0,0.7)' }}>
                <Flame className="h-4 w-4 text-orange-400" />
                <span className="text-white font-bold">{viewOnceCountdown}</span>
              </div>
            )}
            
            {/* Close hint */}
            {viewOnceMedia?.view_once_duration === 0 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full" style={{ background: 'rgba(0,0,0,0.7)' }}>
                <span className="text-white text-sm">Tap anywhere to close</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Disappearing Messages Settings Dialog */}
      <Dialog open={showDisappearingSettings} onOpenChange={setShowDisappearingSettings}>
        <DialogContent 
          className="border-0 max-w-sm"
          style={{ 
            background: 'rgba(10, 15, 44, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#E5E7EB' }}>
              <Timer className="h-5 w-5" style={{ color: '#A78BFA' }} />
              Disappearing Messages
            </DialogTitle>
            <DialogDescription style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
              Messages will disappear after the set time. For media, use "View once" option when sending.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {[
              { value: '24h', label: '24 hours', icon: '⏰' },
              { value: '7d', label: '7 days', icon: '📅' },
              { value: '30d', label: '30 days', icon: '📆' },
              { value: 'off', label: 'Off', icon: '🔓' },
            ].map(option => (
              <button
                key={option.value}
                onClick={() => updateDisappearingSettings(option.value)}
                className={`w-full p-3 rounded-xl flex items-center justify-between transition-all hover:bg-white/10 ${disappearingTimer === option.value ? 'ring-2 ring-purple-500 bg-purple-500/10' : ''}`}
                style={{ background: disappearingTimer === option.value ? 'rgba(124,58,237,0.15)' : 'rgba(255,255,255,0.05)' }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg">{option.icon}</span>
                  <span style={{ color: '#E5E7EB' }}>{option.label}</span>
                </div>
                {disappearingTimer === option.value && (
                  <CheckCheck className="h-5 w-5 text-purple-400" />
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Call Interface */}
      {showCallInterface && conversation && user && (
        <CallInterface
          conversation={conversation}
          currentUser={user}
          otherUser={getOtherUser()}
          incomingCall={incomingCall}
          socket={socketRef.current}
          pendingOffer={pendingWebrtcOffer}
          initiateCallType={!incomingCall ? callType : null}
          onClose={() => {
            setShowCallInterface(false);
            setIncomingCall(null);
            setPendingWebrtcOffer(null);
          }}
        />
      )}

      {/* Full-Screen Media Viewer Modal */}
      {mediaViewerOpen && mediaViewerContent && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.95)' }}
          onClick={closeMediaViewer}
        >
          {/* Close button */}
          <button
            onClick={closeMediaViewer}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="h-6 w-6 text-white" />
          </button>
          
          {/* Media container */}
          <div 
            className="max-w-full max-h-full p-4"
            onClick={(e) => e.stopPropagation()}
          >
            {mediaViewerContent.type === 'image' && (
              <img 
                src={mediaViewerContent.url}
                alt="Full size"
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
              />
            )}
            {mediaViewerContent.type === 'video' && (
              <video 
                src={mediaViewerContent.url}
                controls
                autoPlay
                className="max-w-full max-h-[90vh] rounded-lg"
              />
            )}
            {mediaViewerContent.type === 'audio' && (
              <div className="bg-white/10 rounded-xl p-8 flex flex-col items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-purple-500/30 flex items-center justify-center">
                  <Volume2 className="h-10 w-10 text-purple-300" />
                </div>
                <audio 
                  src={mediaViewerContent.url}
                  controls
                  autoPlay
                  className="w-64"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
